import { ConnectProto } from "@racoon/api";
import { fromTimestamp } from "@racoon/lib";

/**
 * ENDED and CANCELLED both close a shift: no actions, no further transitions.
 *
 * Written as a helper because the obvious spelling, `status !== ENDED`, silently
 * reads a cancelled shift as still running — it occupies its truck and driver,
 * blocks the next shift, and shows the running-shift affordances.
 */
export const isShiftTerminal = (status: ConnectProto.DriverOnShiftStatus) =>
  status === ConnectProto.DriverOnShiftStatus.ENDED ||
  status === ConnectProto.DriverOnShiftStatus.CANCELLED;

/** Approved but not started: it holds its truck and driver without occupying them. */
export const isShiftPlanned = (status: ConnectProto.DriverOnShiftStatus) =>
  status === ConnectProto.DriverOnShiftStatus.NOT_APPROVED;

/**
 * On shift right now: started and not closed. The three predicates partition the
 * enum, so a new status joins exactly one of them instead of every call site.
 */
export const isShiftRunning = (status: ConnectProto.DriverOnShiftStatus) =>
  !isShiftTerminal(status) && !isShiftPlanned(status);

/**
 * A driver can be assigned only to a planned shift that has none: a running
 * shift cannot lack one, and an ended or cancelled one takes no more changes.
 */
export const canAssignShiftDriver = (
  shift: Pick<ConnectProto.DriverOnShift, "driverId" | "status">,
): boolean => !shift.driverId && isShiftPlanned(shift.status);

/**
 * The TTN is approved: the backend stamped it done (`consignmentNoteDoneAt`, once
 * the shift, every trip and every service act are filled) on a shift that has
 * ended. The one check for it — never read the stamp by hand.
 */
export const isTtnApproved = (shift: ConnectProto.DriverOnShift | undefined) =>
  Boolean(shift?.consignmentNoteDoneAt) && shift?.status === ConnectProto.DriverOnShiftStatus.ENDED;

/**
 * When a shift holds its truck. A running one stays open past its plan, so it keeps today's
 * routes. A closed one may lack a completion time (cancelled before any route ran): it ends
 * at its plan, or at its start when it has none or the plan ended earlier.
 */
export type ShiftTimes = Pick<
  ConnectProto.DriverOnShift,
  "startedAt" | "completedAt" | "twStartAt" | "twEndAt"
> &
  Partial<Pick<ConnectProto.DriverOnShift, "status">>;

export const shiftWindow = (shift: ShiftTimes): { start?: Date; end?: Date } => {
  const at = (t: ConnectProto.DriverOnShift["startedAt"]) => fromTimestamp(t) ?? undefined;
  const start = at(shift.startedAt) ?? at(shift.twStartAt);
  if (at(shift.completedAt)) return { start, end: at(shift.completedAt) };
  if (!at(shift.startedAt)) return { start, end: at(shift.twEndAt) };
  if (shift.status === undefined || !isShiftTerminal(shift.status)) return { start };
  const twEnd = at(shift.twEndAt);
  return { start, end: twEnd && start && twEnd > start ? twEnd : start };
};
